import { useLang } from "@/context/LanguageContext";
import { BookOpen, Info, ExternalLink } from "lucide-react";

/* eslint-disable no-unused-vars */
const _icons = { BookOpen };

export default function Book() {
  const { t } = useLang();

  const ptRows = [
    { k: "pt_title_label", v: "pt_title", full: true },
    { k: "authors_label", v: "authors" },
    { k: "edition_label", v: "edition" },
    { k: "published", v: "published_value" },
    { k: "isbn", v: "isbn_value" },
    { k: "formats", v: "formats_value" },
    { k: "length", v: "length_value" },
    { k: "asin", v: "asin_value" },
  ];

  const enRows = [
    { k: "en_title_label", v: "en_title", full: true },
    { k: "authors_label", v: "en_authors" },
    { k: "edition_label", v: "en_edition_value" },
    { k: "en_language_label", v: "en_language_value" },
    { k: "en_pubdate_label", v: "en_pubdate_value" },
    { k: "formats", v: "en_formats_value" },
    { k: "length", v: "en_length_value" },
    { k: "asin", v: "en_asin_value" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20" data-testid="book-page">
      <h1 className="text-4xl sm:text-5xl font-bold">{t.book.title}</h1>
      <p className="mt-4 text-slate-300 max-w-3xl">{t.book.sub}</p>

      <div className="mt-12 grid lg:grid-cols-3 gap-6">
        {/* Verified book cover */}
        <div className="card-mift p-6 flex flex-col items-center justify-center min-h-[420px]" data-testid="book-cover">
          <img
            src="/assets/mift_book_cover.png"
            alt="MIFT — Metodologia Integrada de Mediação em Famílias Transnacionais"
            className="w-full max-w-[240px] rounded-md shadow-[0_20px_60px_-15px_rgba(37,99,235,0.35)]"
            data-testid="book-cover-image"
          />
          <p className="mt-4 text-xs text-slate-500 text-center">{t.book.cover_caption}</p>
        </div>

        <div className="lg:col-span-2 grid gap-4">
          {/* Verified Portuguese edition */}
          <div className="card-mift p-6" data-testid="book-pt-edition">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-blue-300">{t.book.pt_edition}</div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                verified
              </span>
            </div>
            <h3 className="mt-3 font-semibold text-slate-100 leading-snug" data-testid="book-pt-title">
              {t.book.pt_title}
            </h3>
            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {ptRows.map((r) => (
                <div key={r.k} className={r.full ? "sm:col-span-3" : ""}>
                  <dt className="text-slate-500 text-xs uppercase tracking-wider">{t.book[r.k]}</dt>
                  <dd className="text-slate-200 mt-0.5" data-testid={`book-${r.k}`}>{t.book[r.v]}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 grid gap-2 text-xs text-slate-500">
              <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5" /> {t.book.publisher_pending}</div>
              <div className="flex items-start gap-2"><Info className="w-3.5 h-3.5 mt-0.5" /> {t.book.date_pending}</div>
            </div>
            <a
              href="https://www.amazon.com.br/dp/B0GTJ19BD7"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="book-purchase-amazon-br"
              className="mt-5 inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 underline underline-offset-4"
            >
              {t.book.purchase_amazon_br} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Verified English edition */}
          <div className="card-mift p-6" data-testid="book-en-edition">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-blue-300">{t.book.en_edition}</div>
              <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                verified
              </span>
            </div>
            <h3 className="mt-3 font-semibold text-slate-100 leading-snug" data-testid="book-en-title">
              {t.book.en_title}
            </h3>
            <dl className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
              {enRows.map((r) => (
                <div key={r.k} className={r.full ? "sm:col-span-3" : ""}>
                  <dt className="text-slate-500 text-xs uppercase tracking-wider">{t.book[r.k]}</dt>
                  <dd className="text-slate-200 mt-0.5" data-testid={`book-${r.k}`}>{t.book[r.v]}</dd>
                </div>
              ))}
            </dl>
            <a
              href="https://www.amazon.com/INTEGRATED-MEDIATION-METHODOLOGY-TRANSNATIONAL-FAMILIES-ebook/dp/B0HKZZPP1Z/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="book-purchase-amazon-us"
              className="mt-5 inline-flex items-center gap-2 text-sm text-blue-300 hover:text-blue-200 underline underline-offset-4"
            >
              {t.book.purchase_amazon_us} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="card-mift p-6" data-testid="book-overview">
            <div className="text-xs uppercase tracking-widest text-blue-300">{t.book.overview}</div>
            <p className="mt-3 text-slate-300 leading-relaxed">{t.book.overview_body}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
